import { useQuery, useQueryClient } from "@tanstack/react-query";
import localforage from "localforage";
import { useRouter } from "next/router";
import {
	getOperationalLocationsOfflineQueryKey,
	getOperationalLocationsQueryKey,
} from "./use-get-operational-locations";
import { LocationFragment } from "./fragments/location";
import { getCurrentVenueCache } from "./use-get-current-venue";
import { gqlReq } from "~/libs/gql";
import {
	GetLocationByIdDocument,
	GetLocationByIdQuery,
	GetOperationalLocationsQuery,
} from "~/types/__generated/gql/graphql";
import { queryClient } from "~/providers/query-provider";
import { STORAGE_KEYS } from "~/libs/storage-keys";

void (
	/* GraphQL */ `
		query GetLocationById($id: ObjectID!) {
			getLocationById(id: $id) {
				...LocationFragment
			}
		}
		${LocationFragment}
	`
);

export const getLocationByIdQueryKey = (id: string) => ["getLocationById", id];

export const getLocationByIdQueryFn = async (
	id: string,
	signal?: AbortSignal,
) => {
	const variables = { id };
	return gqlReq({
		document: GetLocationByIdDocument,
		variables,
		signal,
	});
};

export const getLocationByIdOfflineQueryKey = (id: string) => [
	"getLocationById",
	"offline",
	id,
];

const updateLocation =
	(location: GqlResultOf<GetLocationByIdQuery>) =>
	(locations: GqlResultOf<GetOperationalLocationsQuery> | undefined) => {
		if (!locations) return locations;
		return locations.map((l) => (l._id === location._id ? location : l));
	};

const upsertLocation =
	(location: GqlResultOf<GetLocationByIdQuery>) =>
	(locations: GqlResultOf<GetOperationalLocationsQuery> | undefined) => {
		if (!locations) return locations;
		return locations.some((l) => l._id === location._id)
			? updateLocation(location)(locations)
			: [...locations, location];
	};

export const useGetLocationById = (id?: string) => {
	return useQuery({
		enabled: !!id,
		queryKey: getLocationByIdQueryKey(id!),
		queryFn: ({ signal }) => getLocationByIdQueryFn(id!, signal),
		onSuccess: (data) => {
			queryClient.setQueryData<GqlResultOf<GetLocationByIdQuery>>(
				getLocationByIdOfflineQueryKey(data._id),
				data,
			);
			queryClient.setQueryData<GqlResultOf<GetOperationalLocationsQuery>>(
				getOperationalLocationsQueryKey(),
				updateLocation(data),
			);
			queryClient.setQueryData<GqlResultOf<GetOperationalLocationsQuery>>(
				getOperationalLocationsOfflineQueryKey(),
				upsertLocation(data),
			);
		},
	});
};

export const useGetLocationByIdOffline = (id?: string) => {
	const queryClient = useQueryClient();

	return useQuery({
		enabled: !!id,
		queryKey: getLocationByIdOfflineQueryKey(id!),
		queryFn: () =>
			queryClient.getQueryData<GqlResultOf<GetLocationByIdQuery>>(
				getLocationByIdOfflineQueryKey(id!),
				{ exact: true },
			),
	});
};

export const useGetCurrentLocation = () => {
	const {
		query: { id },
	} = useRouter();

	return useGetLocationById(id === "new" ? undefined : String(id ?? ""));
};

export const useGetCurrentLocationOffline = () => {
	const { query } = useRouter();

	// @ts-ignore
	return useGetLocationByIdOffline(query.id);
};

const storeLocation = (data: GqlResultOf<GetLocationByIdQuery>) => {
	queryClient.setQueryData<GqlResultOf<GetLocationByIdQuery>>(
		getLocationByIdQueryKey(data._id),
		data,
	);
	queryClient.setQueryData<GqlResultOf<GetLocationByIdQuery>>(
		getLocationByIdOfflineQueryKey(data._id),
		data,
	);
	queryClient.setQueryData<GqlResultOf<GetOperationalLocationsQuery>>(
		getOperationalLocationsQueryKey(),
		updateLocation(data),
	);
	queryClient.setQueryData<GqlResultOf<GetOperationalLocationsQuery>>(
		getOperationalLocationsOfflineQueryKey(),
		upsertLocation(data),
	);
};

/** The latest in-flight location fetch per id, so subscription events can wait for it. */
const locationFetches = new Map<string, Promise<void>>();

export const fetchAndStoreGetLocationByIdQuery = (id: string) => {
	const fetch = getLocationByIdQueryFn(id)
		.then((data) => {
			if (locationFetches.get(id) === fetch) storeLocation(data);
		})
		.finally(() => {
			if (locationFetches.get(id) === fetch) locationFetches.delete(id);
		});
	locationFetches.set(id, fetch);
	return fetch;
};

/** What a subscription event proves about the location's server state. */
interface LocationFreshness {
	updatedAt: string;
	orderId?: string;
}

const isCachedLocationFresh = (
	id: string,
	{ updatedAt, orderId }: LocationFreshness,
) => {
	const location = queryClient.getQueryData<GqlResultOf<GetLocationByIdQuery>>(
		getLocationByIdOfflineQueryKey(id),
	);
	const cachedUpdatedAt = orderId
		? location?._current_orders.find((order) => order._id === orderId)
				?.updated_at
		: location?.updated_at;
	return (
		!!cachedUpdatedAt &&
		new Date(cachedUpdatedAt).getTime() >= new Date(updatedAt).getTime()
	);
};

/** Offline-enabled venues refetch, unless the cached offline detail already reflects the event. */
export const syncLocationById = async (
	id: string,
	freshness: LocationFreshness,
) => {
	// Without downloaded offline data, or while queued offline actions are not
	// synced yet (a fetch would drop them from the offline cache), only refresh
	// the online detail.
	if (
		!getCurrentVenueCache()?.is_offline_mode_enabled ||
		!queryClient.getQueryData(getOperationalLocationsOfflineQueryKey()) ||
		(await localforage.getItem<unknown[]>(STORAGE_KEYS.OFFLINE_MUTATIONS))
			?.length
	) {
		void queryClient.invalidateQueries(getLocationByIdQueryKey(id), {
			exact: true,
		});
		return;
	}

	await locationFetches.get(id)?.catch(() => undefined);
	if (isCachedLocationFresh(id, freshness)) return;
	// A fetch started after this event arrived already reflects it.
	await (
		locationFetches.get(id) ?? fetchAndStoreGetLocationByIdQuery(id)
	).catch(console.error);
};
